import type { Brand } from './brand';
import type { EventType } from './events';
import type { File } from './files';
import type { BaseFolder } from './folders';
import type { Image } from './images';
import type {
  ChecklistResult,
  InspectionChecklist,
} from './inspectionChecklist';
import type { Category, Model } from './model';
import type { Tag } from './tag';
import type { BaseUser } from './user';

export const HardwareStatus = {
  IN_STOCK: 'IN_STOCK',
  DAMAGED: 'DAMAGED',
  QUARANTINE: 'QUARANTINE',
  LOST: 'LOST',
  TRASH: 'TRASH',
} as const;

export type HardwareStatus =
  (typeof HardwareStatus)[keyof typeof HardwareStatus];

export const ExpirationStatus = {
  VALID: 'VALID',
  NOTIFY: 'NOTIFY',
  EXPIRED: 'EXPIRED',
} as const;

export type ExpirationStatus =
  (typeof ExpirationStatus)[keyof typeof ExpirationStatus];

export const InspectionStatus = {
  UP_TO_DATE: 'UP_TO_DATE',
  NOTIFY: 'NOTIFY',
  LATE: 'LATE',
} as const;

export type InspectionStatus =
  (typeof InspectionStatus)[keyof typeof InspectionStatus];

export const GeneralStatus = {
  IN_STOCK: 'IN_STOCK',
  CONFORM: 'CONFORM',
  NOTIFY: 'NOTIFY',
  DAMAGED: 'DAMAGED',
  NOT_CONFORM: 'NOT_CONFORM',
  QUARANTINE: 'QUARANTINE',
  LOST: 'LOST',
  TRASH: 'TRASH',
} as const;

export type GeneralStatus = (typeof GeneralStatus)[keyof typeof GeneralStatus];

/**
 * Standardised inspection verdict (repair v3). Decoupled from HardwareStatus:
 * availability side-effects are carried by the linked Repair(s), while TRASH/LOST
 * live on HardwareStatus. LOST is deliberately absent — a lost equipment is not
 * inspected (see the `inspect` endpoint / EventType.EQUIPMENT_LOST).
 */
export const InspectionResult = {
  CONFORM: 'CONFORM',
  CONFORM_WITH_RESERVE: 'CONFORM_WITH_RESERVE',
  FAIL: 'FAIL',
} as const;

export type InspectionResult =
  (typeof InspectionResult)[keyof typeof InspectionResult];

/**
 * Result chosen in the inspection UI, incl. the LOST pseudo-result ("could not be
 * inspected" — its actual outcome is picked via InspectionFollowUpAction: TRASH,
 * LOST (genuinely missing) or a non-blocking OPEN_REPAIR).
 */
export type InspectionResultChoice = InspectionResult | 'LOST';

/**
 * Sub-action attached to a verdict; the backend derives status + repair from it.
 * LOST is only a valid follow-up when the top-level result is itself 'LOST'.
 */
export type InspectionFollowUpAction =
  | 'NONE'
  | 'OPEN_REPAIR'
  | 'TRASH'
  | 'LOST';

export type Inspection = {
  id: number | null;
  date: Date;
  status: HardwareStatus;
  comment: string | null;
  userId: number | null;
};

/** A standardised inspection record (replaces EquipmentEvent(INSPECTION)). */
export type EquipmentInspection = {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  date: Date;
  result: InspectionResult;
  comment: string | null;
  organizationId: number;
  equipmentId: number;
  userId: number;
  user?: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    externalUserId: string | null;
  } | null;
  equipment?: {
    id: number;
    name: string;
  } | null;
  checklistResult?: ChecklistResult | null;
  images?: Image[];
};

export type BaseEquipment = {
  createdAt: Date;
  updatedAt: Date;
  name: string;
  description: string | null;
  serialNumber: string | null;
  partNumber: string | null;
  nfcTags: string[];
  price: number | null;
  shareableId: string | null;
  manufactureDate: string | Date | null;
  commissioningDate: string | Date | null;
  expirationDate: string | Date | null;
  nextInspectionDate: string | Date | null;
  lastInspectionDate: string | Date | null;
  inspectionPeriod: number | null;
  manufactureLifeSpan: number | null;
  commissioningLifeSpan: number | null;
  hasExpiration: boolean;
  hardwareStatus: HardwareStatus;
  expirationStatus: ExpirationStatus;
  inspectionStatus: InspectionStatus;
  generalStatus: GeneralStatus;
  lastInspectionResult: InspectionResult | null;
  organizationId: number;
  brandId: number | null;
  modelId: number | null;
  categoryId: number | null;
  userId: number | null;
  folderId: number;
};

export type Equipment = BaseEquipment & {
  id: number;
  Brand: Brand | null;
  Model: Model | null;
  category: Category | null;
  tags: Tag[];
  user: BaseUser | null;
  Folder: BaseFolder;
  files: File[];
  images: Image[];
  inspectionChecklist: InspectionChecklist | null;
};

/**
 * A single entry of the equipment timeline. Repair v3: the timeline is now built
 * from EventLog (not EquipmentEvent). Synthetic entries (MANUFACTURING /
 * COMMISSIONING derived from equipment dates) reuse the same shape with a null id.
 */
export type EquipmentTimelineEntry = {
  id: number | null;
  type: EventType | 'MANUFACTURING' | 'COMMISSIONING';
  createdAt: Date;
  ownerUser?: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    externalUserId: string | null;
  } | null;
  user?: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    externalUserId: string | null;
  } | null;
  repair?: { id: number } | null;
  equipmentInspection?: {
    id: number;
    result: InspectionResult;
    comment: string | null;
    /**
     * When the inspection was carried out — not when its EventLog row was
     * written. The two differ for a backdated inspection (creation form, CSV
     * import); `createdAt` above already carries this date for inspection
     * entries, so the timeline sorts and reads chronologically.
     */
    date: Date;
    /** Who carried out the inspection — `ownerUser` above is who recorded it. */
    user?: {
      id: number;
      firstName: string | null;
      lastName: string | null;
      externalUserId: string | null;
    } | null;
  } | null;
  deletedItemName?: string | null;
};

export type EquipmentLastLocation = {
  latitude: number;
  longitude: number;
  accuracy: number | null;
  capturedAt: Date;
};

export type EquipmentWithHistory = Equipment & {
  eventLogs: EquipmentTimelineEntry[];
  Organization?: {
    currency: string;
  };
  /** Most recent passive GPS capture (inspection or NFC scan), null if none yet. */
  lastLocation: EquipmentLastLocation | null;
};

export const EquipmentLocationSource = {
  INSPECTION: 'INSPECTION',
  SCAN: 'SCAN',
} as const;

export type EquipmentLocationSource =
  (typeof EquipmentLocationSource)[keyof typeof EquipmentLocationSource];

export type EquipmentLocationHistoryItem = {
  id: number;
  latitude: number;
  longitude: number;
  accuracy: number | null;
  capturedAt: Date;
  source: EquipmentLocationSource;
};

export type EquipmentMapPoint = {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  accuracy: number | null;
  capturedAt: Date;
};

export const EquipmentDeadlineFilter = {
  INSPECT_60D: 'INSPECT_60D',
  REPLACE_60D: 'REPLACE_60D',
} as const;

export type EquipmentDeadlineFilter =
  (typeof EquipmentDeadlineFilter)[keyof typeof EquipmentDeadlineFilter];

export type EquipmentListSortField =
  | 'name'
  | 'generalStatus'
  | 'nextInspectionDate'
  | 'expirationDate'
  | 'lastInspectionDate'
  | 'manufactureDate'
  | 'commissioningDate'
  | 'createdAt'
  | 'updatedAt'
  | 'serialNumber'
  | 'partNumber'
  | 'price'
  | 'hasExpiration'
  | 'Brand'
  | 'Model'
  | 'Folder'
  | 'user';

export type EquipmentListQuery = {
  page?: number;
  take?: number;
  folderIds?: number[];
  userIds?: number[];
  tagIds?: number[];
  equipmentIds?: number[];
  tagMode?: 'AND' | 'OR';
  generalStatuses?: GeneralStatus[];
  deadline?: EquipmentDeadlineFilter | null;
  search?: string;
  sortField?: EquipmentListSortField;
  sortDirection?: 'asc' | 'desc';
  includeFacets?: boolean;
  includeData?: boolean;
};

export type EquipmentListUser = Pick<
  BaseUser,
  'id' | 'firstName' | 'lastName' | 'externalUserId'
>;

export type EquipmentListItem = Pick<
  BaseEquipment,
  | 'createdAt'
  | 'updatedAt'
  | 'name'
  | 'serialNumber'
  | 'partNumber'
  | 'nfcTags'
  | 'price'
  | 'manufactureDate'
  | 'commissioningDate'
  | 'expirationDate'
  | 'nextInspectionDate'
  | 'lastInspectionDate'
  | 'inspectionPeriod'
  | 'manufactureLifeSpan'
  | 'commissioningLifeSpan'
  | 'hasExpiration'
  | 'hardwareStatus'
  | 'expirationStatus'
  | 'inspectionStatus'
  | 'generalStatus'
  | 'userId'
  | 'folderId'
> & {
  id: number;
  Brand: Pick<Brand, 'id' | 'name'> | null;
  Model: Pick<Model, 'id' | 'name'> | null;
  Folder: BaseFolder;
  tags: Pick<Tag, 'id' | 'name' | 'category'>[];
  user: EquipmentListUser | null;
  images: Image[];
};

export type EquipmentListResponse = {
  data: EquipmentListItem[];
  total: number;
  page: number;
  take: number;
  facets?: {
    tags: {
      id: number;
      name: string;
      category: Tag['category'];
      count: number;
    }[];
    users: {
      id: number;
      firstName: string;
      lastName: string;
      email: string | null;
      count: number;
    }[];
    generalStatuses: {
      value: GeneralStatus;
      count: number;
    }[];
    deadlines: {
      value: EquipmentDeadlineFilter;
      count: number;
    }[];
  };
};

export type EquipmentLookupMatch = {
  id: number;
  folderId: number;
  name: string;
  serialNumber: string | null;
  imageSrc: string | null;
};

export type EquipmentLookupResponse =
  | { type: 'single'; equipment: EquipmentLookupMatch }
  | { type: 'multiple'; count: number; equipments: EquipmentLookupMatch[] }
  | { type: 'none' };
